import { useEffect, useRef } from 'react';
import { DESTINATIONS } from './destinations.js';

/**
 * R12 — the ONE keyboard registry. A single list both BINDS the shortcuts (via
 * useKeyboardShortcuts) AND renders the `?` help overlay, so an advertised-but-
 * dead shortcut is structurally impossible: the help shows exactly what the hook
 * binds. Replaces the old split between useKeyboardTriage's switch and a parallel
 * KEYBOARD_SHORTCUTS constant.
 *
 * Two kinds: single-key (j/↓, k/↑, r, a, /, ?) and a `g`-prefixed sequence for
 * navigation (g i → Inbox, …). Shortcut ids never use the bare `send` token (the
 * no-send static guard) and no shortcut sends / replies / triggers a write.
 */
export type ShortcutGroup = 'Navigate' | 'Triage' | 'Help';

export interface Shortcut {
  /** Stable id (never the bare send token). */
  id: string;
  /** Display chips, e.g. ['J', '↓'] or ['g', 'i']. */
  keys: string[];
  /** The KeyboardEvent.key value(s) (lowercased) that fire it. For a sequence,
   *  this is the SECOND key (the one after `g`). */
  trigger: string[];
  /** When set, the trigger only fires right after this prefix key. */
  sequence?: 'g';
  label: string;
  group: ShortcutGroup;
  run: () => void;
}

export interface ShortcutHandlers {
  selectNext: () => void;
  selectPrev: () => void;
  focusDraft: () => void;
  focusSearch: () => void;
  analyze: () => void;
  toggleHelp: () => void;
  // A2 — triage verbs for the open thread (inbox / follow-ups). Reversible only
  // (no send keybind): Done, Snooze (opens the picker), Mute. They drive the
  // existing canvas triage buttons, so the undo toast + auto-advance come for free.
  triageDone: () => void;
  triageSnooze: () => void;
  triageMute: () => void;
  // g-key navigation to the routed view destinations (EB4 routed all four outreach
  // hubs, so they now have their own g-keys).
  goInbox: () => void;
  goFollowups: () => void;
  goEngagement: () => void;
  goJobChanges: () => void;
  goConnectionRequests: () => void;
  goKeepInTouch: () => void;
  goContacts: () => void;
  goIdentityMatches: () => void;
  goSetup: () => void;
}

/** Build the registry from the app's handlers. The labels come from the R11
 *  destination registry, so the keyboard names match the rail + tab title. */
export function buildShortcuts(h: ShortcutHandlers): Shortcut[] {
  const nav: Array<{ id: string; key: string; label: string; run: () => void }> = [
    { id: 'inbox', key: 'i', label: DESTINATIONS.inbox.label, run: h.goInbox },
    { id: 'followups', key: 'f', label: DESTINATIONS.followups.label, run: h.goFollowups },
    // EB4 — the routed outreach views. `g c` is Connection requests (so Contacts is
    // `g o`, per the mockup).
    { id: 'engagement', key: 'e', label: DESTINATIONS.engagement.label, run: h.goEngagement },
    { id: 'job-changes', key: 'j', label: DESTINATIONS.jobChanges.label, run: h.goJobChanges },
    {
      id: 'connection-requests',
      key: 'c',
      label: DESTINATIONS.connectionRequests.label,
      run: h.goConnectionRequests,
    },
    { id: 'keep-in-touch', key: 't', label: DESTINATIONS.keepInTouch.label, run: h.goKeepInTouch },
    { id: 'contacts', key: 'o', label: DESTINATIONS.contacts.label, run: h.goContacts },
    {
      id: 'identity-matches',
      key: 'm',
      label: DESTINATIONS.identityMatches.label,
      run: h.goIdentityMatches,
    },
    { id: 'setup', key: 's', label: DESTINATIONS.setup.label, run: h.goSetup },
  ];

  return [
    ...nav.map(
      (n): Shortcut => ({
        id: `go-${n.id}`,
        keys: ['g', n.key],
        trigger: [n.key],
        sequence: 'g',
        label: `Go to ${n.label}`,
        group: 'Navigate',
        run: n.run,
      }),
    ),
    {
      id: 'select-next',
      keys: ['J', '↓'],
      trigger: ['j', 'arrowdown'],
      label: 'Select next thread',
      group: 'Triage',
      run: h.selectNext,
    },
    {
      id: 'select-prev',
      keys: ['K', '↑'],
      trigger: ['k', 'arrowup'],
      label: 'Select previous thread',
      group: 'Triage',
      run: h.selectPrev,
    },
    {
      id: 'focus-draft',
      keys: ['R'],
      trigger: ['r'],
      label: 'Focus the reply editor',
      group: 'Triage',
      run: h.focusDraft,
    },
    {
      id: 'analyze',
      keys: ['A'],
      trigger: ['a'],
      label: 'Analyze the current thread',
      group: 'Triage',
      run: h.analyze,
    },
    {
      id: 'triage-done',
      keys: ['D'],
      trigger: ['d'],
      label: 'Done — no reply needed (open thread)',
      group: 'Triage',
      run: h.triageDone,
    },
    {
      id: 'triage-snooze',
      keys: ['Z'],
      trigger: ['z'],
      label: 'Snooze the open thread (opens the picker)',
      group: 'Triage',
      run: h.triageSnooze,
    },
    {
      id: 'triage-mute',
      keys: ['M'],
      trigger: ['m'],
      label: 'Mute the open conversation',
      group: 'Triage',
      run: h.triageMute,
    },
    {
      id: 'focus-search',
      keys: ['/'],
      trigger: ['/'],
      label: 'Focus search',
      group: 'Triage',
      run: h.focusSearch,
    },
    {
      id: 'toggle-help',
      keys: ['?'],
      trigger: ['?'],
      label: 'Show this overlay (Esc closes)',
      group: 'Help',
      run: h.toggleHelp,
    },
  ];
}

const FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** True for typing surfaces — shortcuts are suppressed there so 'j' in a draft
 *  types a j, not "next thread". */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (FORM_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  const ce = target.contentEditable;
  if (ce === 'true' || ce === 'plaintext-only') return true;
  const attr = target.getAttribute('contenteditable');
  if (attr === '' || attr === 'true' || attr === 'plaintext-only') return true;
  return false;
}

/** Window for the second key of a `g` sequence before it lapses. */
const SEQUENCE_MS = 1200;

/**
 * Bind the registry. Single-key shortcuts fire on their trigger; `g`-sequence
 * shortcuts fire when their key follows a `g` within SEQUENCE_MS. Modifier
 * presses and typing surfaces are ignored (Esc-to-close the help is the Dialog
 * primitive's own job, so it isn't a registry entry).
 */
export function useKeyboardShortcuts(shortcuts: Shortcut[], opts?: { enabled?: boolean }): void {
  const enabled = opts?.enabled ?? true;
  // Latest-ref: the handlers (and the registry) change identity every render
  // (their closures read live state), but the listener must bind ONCE so the
  // `g`-pending state survives the frequent re-renders (polling, etc.). We read
  // the current registry from the ref on each keydown — cheap for ~13 entries.
  const ref = useRef(shortcuts);
  ref.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;

    let gPending = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const clearPending = () => {
      gPending = false;
      if (timer) clearTimeout(timer);
    };
    const armG = () => {
      clearPending();
      gPending = true;
      timer = setTimeout(clearPending, SEQUENCE_MS);
    };

    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      const key = e.key.toLowerCase();
      // R12 review — Shift must NOT turn a capital letter into its shortcut
      // (Shift+J selecting the next thread is a misfire). But Shift IS how the
      // symbol triggers are produced (`?` = Shift+/), so only gate shifted
      // LETTERS, never symbols.
      if (e.shiftKey && /^[a-z]$/.test(key)) return;

      const single = new Map<string, () => void>();
      const sequenced = new Map<string, () => void>();
      for (const s of ref.current) {
        const target = s.sequence ? sequenced : single;
        for (const t of s.trigger) target.set(t, s.run);
      }

      if (gPending) {
        const run = sequenced.get(key);
        if (run) {
          e.preventDefault();
          clearPending();
          run();
          return;
        }
        if (key === 'g') {
          armG(); // re-arm on a repeated g
          e.preventDefault();
          return;
        }
        // A non-target second key cancels the sequence (a deliberate `g` prefix
        // SWALLOWS it, Gmail/GitHub-style) rather than firing as a single key.
        clearPending();
        return;
      }

      if (key === 'g' && sequenced.size > 0) {
        armG();
        e.preventDefault();
        return;
      }

      const run = single.get(key);
      if (run) {
        e.preventDefault();
        run();
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearPending();
    };
  }, [enabled]);
}
